import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';
import {Const, BASIC_CONDITION_REASONS} from "../../../core/helpers/Const";
import {DateRange, FdDate, DatetimeAdapter, Nullable} from "@fundamental-ngx/core";
import {DialogService} from "../../../service/services/misc/dialog/dialog.service";
import {SavedFilterViewsService} from "../../../service/services/misc/saved-filter-views/saved-filter-views.service";
import {AdvancedSearchFilters} from "../../models/advanced-search-filters.model";
import {SaveFilterViewDialogComponent} from "../../dialogs/save-filter-view-dialog/save-filter-view-dialog.component";
import {HttpClient} from "@angular/common/http";

export { AdvancedSearchFilters };

export interface SearchField {
    jsonPath: string;
    label: string;
    type?: string;
    placeholder?: string;
    description?: string;
    nestedPaths?: string[]
}

export interface ResourceTypeOption {
    GVT: {
        group: string;
        version: string;
        type: string;
    };
    Name: string;
    AllowedActions: string[];
    searchFields?: SearchField[];
    jsonSchema?: any;
}

const MAX_RESOURCE_TYPES = 100;

@Component({
    selector: 'app-advanced-search',
    templateUrl: './advanced-search.component.html',
    styleUrls: ['./advanced-search.component.scss'],
    standalone: false
})
export class AdvancedSearchComponent implements OnInit, OnDestroy, OnChanges {
    @Input() currentOrg: string;
    @Input() currentPath: string;
    @Input() availableResourceTypes: ResourceTypeOption[] = [];
    @Input() resultCount: number = 0;
    @Input() loadSavedFilters: AdvancedSearchFilters | null = null;
    @Input() currentViewName: string = 'Standard'; // Current filter view name
    @Output() close = new EventEmitter<void>();
    @Output() applyFilters = new EventEmitter<AdvancedSearchFilters>();
    @Output() clearContent = new EventEmitter();
    @Output() cancelSearch = new EventEmitter<void>(); // Emits when search should be cancelled
    @Output() viewSaved = new EventEmitter<string>(); // Emits the name of the saved view
    @Output() filtersModified = new EventEmitter<void>(); // Emits when user manually changes filters

    @ViewChild('labelValuesInput', { read: ElementRef }) labelValuesInput: ElementRef;

    filters: AdvancedSearchFilters = {
        recursiveSearch: true,
        status: '' // Default to "All Statuses"
    };

    selectedRange: Nullable<DateRange<FdDate>>;
    createdOnSelectedRange: Nullable<DateRange<FdDate>>;
    changedOnSelectedRange: Nullable<DateRange<FdDate>>;
    monthYearDateFormat = { year: 'numeric', month: '2-digit', day: '2-digit' };
    createdOnDateRanges = { start: '', end: '' };
    changedOnDateRanges = { start: '', end: '' };


    filteredView = 'none';

    resourceTypes: ResourceTypeOption[] = [];
    filteredResourceTypes: ResourceTypeOption[] = [];
    selectedResourceType: ResourceTypeOption | null = null;

    statuses: string[] = [
        '',
        // Primary lifecycle states first
        // All other statuses alphabetically
        ...BASIC_CONDITION_REASONS
    ]; // Empty string for "All Statuses"
    // Map server keys to display names - empty string maps to "All Statuses"
    statusDisplayNames: { [key: string]: string } = {
        '': 'All Statuses',
        ...BASIC_CONDITION_REASONS.reduce((acc, key) => {
            acc[key] = key;
            return acc;
        }, {} as { [key: string]: string })
    };
    dynamicSearchFields: SearchField[] = [];
    dynamicFieldValues: { [key: string]: any } = {};
    isRecursiveDisabled = false;

    // Label values token handling
    labelValueTokens: string[] = [];
    labelValueInput: string = '';
    labelValuePendingText: string = ''; // Track text being typed

    private searchSubject$ = new Subject<string>();
    private resourceTypeSubject$ = new Subject<ResourceTypeOption>();
    private schemaLoadedSubject$ = new Subject<void>();
    private subscriptions: Subscription[] = [];
    private isLoadingFilters = false; // Flag to prevent triggering filtersChanged during load
    private loadedFiltersSnapshot: string | null = null; // Snapshot of filters at load time for dirty detection
    private lastKeyPressed: string | null = null; // Track the last key that was pressed

    // Custom filter function that returns all items (we handle filtering ourselves)
    customFilterFn = (contentArray: any[], searchTerm: string): any[] => {
        if (this.lastKeyPressed === 'ArrowDown') {
            return this.filteredResourceTypes;
        }

        // If no search term, return all
        if (!searchTerm) {
            return [];
        }

        const term = searchTerm.toLowerCase();

        this.performFilter(term);
        return this.filteredResourceTypes;
    };

    constructor(
        private datetimeAdapter: DatetimeAdapter<FdDate>,
        private dialogService: DialogService,
        private savedFilterViewsService: SavedFilterViewsService,
        private httpClient: HttpClient,
        private cdr: ChangeDetectorRef
    ) {
        const today = this.datetimeAdapter.today();
        this.selectedRange = null;
        this.createdOnSelectedRange = null;
        this.changedOnSelectedRange = null;
        this.createdOnDateRanges = { start: '', end: '' };
        this.changedOnDateRanges = { start: '', end: '' };
    }

    private pendingFilters: AdvancedSearchFilters | null = null;
    private initialized = false;

    ngOnInit(): void {
        this.resourceTypes = this.availableResourceTypes;

        // Start with first 50 items
        this.filteredResourceTypes = this.resourceTypes.slice(0, 50);

        this.checkIfResourceGroup();

        // Set up resource type selection stream with switchMap to cancel previous schema requests
        const resourceTypeSub = this.resourceTypeSubject$.pipe(
            switchMap(resourceType => {
                const gvt = `${resourceType.GVT.group}/${resourceType.GVT.version}/${resourceType.GVT.type}`;
                const schemaUrl = `/api/resources/resourceapi/.schema?resourcesType=${encodeURIComponent(gvt)}/`;

                return this.httpClient.get<any>(schemaUrl);
            })
        ).subscribe({
            next: (schema) => {
                if (this.selectedResourceType) {
                    const oJsonSchema = schema.details && schema.details.schema && schema.details.schema.jsonSchema;
                    this.handleSearchFields(this.selectedResourceType.searchFields, oJsonSchema);
                }
                this.schemaLoadedSubject$.next();
            },
            error: (error) => {
                console.error('Error fetching schema:', error);
                if (this.selectedResourceType) {
                    this.handleSearchFields(this.selectedResourceType.searchFields);
                }
                this.schemaLoadedSubject$.next();
            }
        });

        this.subscriptions.push(resourceTypeSub);

        this.initialized = true;

        // Apply any filters that arrived via ngOnChanges before ngOnInit ran
        if (this.pendingFilters) {
            this.loadFiltersFromSaved(this.pendingFilters);
            this.pendingFilters = null;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Update resourceTypes if availableResourceTypes changed
        if (changes['availableResourceTypes'] && changes['availableResourceTypes'].currentValue) {
            this.resourceTypes = this.availableResourceTypes;
            this.filteredResourceTypes = this.resourceTypes.slice(0, 50);
        }

        // Load saved filters if provided, or reset if set to null or reset token
        if (changes['loadSavedFilters']) {
            const savedFilters = changes['loadSavedFilters'].currentValue;
            if (savedFilters && !savedFilters.__reset) {
                if (this.initialized) {
                    this.loadFiltersFromSaved(savedFilters);
                } else {
                    // ngOnChanges fires before ngOnInit — defer until initialized
                    this.pendingFilters = savedFilters;
                }
            } else if ((savedFilters === null || savedFilters?.__reset) && !changes['loadSavedFilters'].firstChange) {
                // Reset filters to default when explicitly set to null or reset token (but not on first load)
                this.resetFilters();
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private async loadFiltersFromSaved(savedFilters: AdvancedSearchFilters): Promise<void> {
        // Cancel any in-progress searches when switching filter views
        this.cancelSearch.emit();

        // Set flag to prevent triggering filtersChanged event during load
        this.isLoadingFilters = true;

        // Reset all transient control state before applying the new view
        this.resetFilters();

        // Apply the saved filters
        this.filters = { ...savedFilters };

        // Find and set the resource type, and wait for schema to load
        if (savedFilters.resourceType) {
            const resourceType = this.resourceTypes.find(rt => {
                const rtGvt = `${rt.GVT.group}/${rt.GVT.version}/${rt.GVT.type}`;
                return rtGvt === savedFilters.resourceType || rt.Name === savedFilters.resourceTypeName;
            });
            if (resourceType) {
                this.selectedResourceType = resourceType;
                // Wait for schema to load and fields to be initialized
                await this.onResourceTypeSelected(resourceType);
            }
        }

        // Restore dynamic field values AFTER schema has loaded and fields are initialized
        if (savedFilters.dynamicFieldValues) {
            this.dynamicFieldValues = { ...savedFilters.dynamicFieldValues };
        }

        // Set date ranges if present
        if (savedFilters.createdOnStart || savedFilters.createdOnEnd) {
            this.createdOnDateRanges.start = savedFilters.createdOnStart || '';
            this.createdOnDateRanges.end = savedFilters.createdOnEnd || '';
            this.createdOnSelectedRange = {
                start: savedFilters.createdOnStart ? this.fdDateFromIso(savedFilters.createdOnStart) : null,
                end: savedFilters.createdOnEnd ? this.fdDateFromIso(savedFilters.createdOnEnd) : null
            };
        }

        if (savedFilters.changedOnStart || savedFilters.changedOnEnd) {
            this.changedOnDateRanges.start = savedFilters.changedOnStart || '';
            this.changedOnDateRanges.end = savedFilters.changedOnEnd || '';
            this.changedOnSelectedRange = {
                start: savedFilters.changedOnStart ? this.fdDateFromIso(savedFilters.changedOnStart) : null,
                end: savedFilters.changedOnEnd ? this.fdDateFromIso(savedFilters.changedOnEnd) : null
            };
        }

        // Set label values if present
        if (savedFilters.labelValues && Array.isArray(savedFilters.labelValues)) {
            this.labelValueTokens = [...savedFilters.labelValues];
        }

        // Apply the filters after all async operations complete
        this.onApply();
        this.isLoadingFilters = false;
        // Snapshot current state so Apply can detect whether anything actually changed
        this.loadedFiltersSnapshot = this.getFiltersSnapshot();
        // Force change detection — property assignments after `await` may run outside Angular's zone
        this.cdr.detectChanges();
    }

    private resetFilters(): void {
        // Reset filters to default state
        this.filters = {
            recursiveSearch: !this.isRecursiveDisabled,
            status: '' // Default to "All Statuses"
        };

        // Clear resource type selection
        this.selectedResourceType = null;
        this.dynamicSearchFields = [];
        this.dynamicFieldValues = {};

        // Clear date ranges
        this.createdOnSelectedRange = null;
        this.changedOnSelectedRange = null;
        this.createdOnDateRanges = { start: '', end: '' };
        this.changedOnDateRanges = { start: '', end: '' };

        // Clear label values
        this.labelValueTokens = [];
        this.labelValueInput = '';
    }

    private checkIfResourceGroup(): void {
        // Check if we're in a resource group by examining the current URL
        const url = this.getCurrentUrl();
        const isResourceGroup = url.includes('/resource-groups');

        if (isResourceGroup) {
            this.isRecursiveDisabled = true;
            this.filters.recursiveSearch = false;
        }
    }

    protected getCurrentUrl(): string {
        return window.location.href;
    }

    onSearchTextChange(searchTerm: string): void {
        // If the search term is empty and we have a selected resource type, clear it
        if (!searchTerm && this.selectedResourceType) {
            this.selectedResourceType = null;
            this.dynamicSearchFields = [];
            this.dynamicFieldValues = {};
        }
        // Push search term to debounced subject
        this.searchSubject$.next(searchTerm);
    }

    onComboboxKeydown(event: any): void {
        const key = event.key;

        // Track the last key pressed
        this.lastKeyPressed = key;

        if (key === 'Enter') {
            this.onResourceTypeEnter(event);
        }
    }

    onResourceTypeEnter(event: Event): void {
        // When user presses Enter, find and select the first matching resource type
        const inputElement = event.target as HTMLInputElement;
        const searchTerm = (inputElement.value)?.toLowerCase();

        if (!searchTerm) {
            return;
        }

        // Try to find an exact match first (by name or display format)
        let matchedType = this.filteredResourceTypes.find(rt => {
            const name = (rt.Name || rt.GVT.type).toLowerCase();
            const displayFormat = this.displayResourceTypeFunc(rt).toLowerCase();
            return name === searchTerm || displayFormat === searchTerm;
        });

        // If no exact match, use the first filtered result
        if (!matchedType && this.filteredResourceTypes.length > 0) {
            matchedType = this.filteredResourceTypes[0];
        }

        if (matchedType) {
            // Clear navigation state

            // Manually set the selected resource type - this will trigger ngModelChange
            this.selectedResourceType = matchedType;
            // Prevent default Enter behavior
            event.preventDefault();
            // Trigger the selection handler
            this.onResourceTypeSelected(matchedType);
        }
    }

    private performFilter(searchTerm: string): void {
        // If search term is empty or too short, show first 50
        if (!searchTerm || searchTerm.length < 2) {
            this.filteredResourceTypes = this.resourceTypes.slice(0, 50);
            return;
        }

        // Trim the search term to only include text before parentheses
        const trimmedTerm = searchTerm.split('(')[0].trim();

        // If trimmed term is too short after removing parentheses, show first 50
        if (trimmedTerm.length < 2) {
            this.filteredResourceTypes = this.resourceTypes.slice(0, 50);
            return;
        }

        const term = trimmedTerm.toLowerCase();

        // Separate matches into name matches and GVT-only matches
        const nameMatches: ResourceTypeOption[] = [];
        const gvtMatches: ResourceTypeOption[] = [];

        this.resourceTypes.forEach(rt => {
            const name = (rt.Name || rt.GVT.type).toLowerCase();
            const gvt = `${rt.GVT.group}/${rt.GVT.version}/${rt.GVT.type}`.toLowerCase();

            if (name.includes(term)) {
                nameMatches.push(rt);
            } else if (gvt.includes(term)) {
                gvtMatches.push(rt);
            }
        });

        // Combine: name matches first, then GVT matches
        const combined = [...nameMatches, ...gvtMatches];

        // Limit to 100 results for performance
        this.filteredResourceTypes = combined.slice(0, MAX_RESOURCE_TYPES);
    }

    displayResourceTypeFunc = (item: ResourceTypeOption | string): string => {
        // If item is a string (user typed text), return it as-is
        if (typeof item === 'string') {
            return item;
        }
        // If item is null/undefined, return empty string
        if (!item) return '';
        // If item doesn't have GVT property, it's not a valid ResourceTypeOption
        if (!item.GVT) {
            return '';
        }
        const name = item.Name?.length ? item.Name : item.GVT.type;
        return `${name} (${item.GVT.group}/${item.GVT.version})`;
    };
    @Input() fullPath!: string;
    @Input() isFclOpen: boolean;

    onResourceTypeSelected(resourceType: ResourceTypeOption | string | null): Promise<void> {
        // If resourceType is null or undefined, clear the selection
        if (resourceType === null || resourceType === undefined || resourceType === '') {
            this.selectedResourceType = null;
            this.dynamicSearchFields = [];
            this.dynamicFieldValues = {};
            return Promise.resolve();
        }

        // Only process if we received a valid ResourceTypeOption object
        if (typeof resourceType === 'string') {
            // User is still typing - don't update selectedResourceType
            return Promise.resolve();
        }

        // Validate that we have a proper ResourceTypeOption with GVT
        if (!resourceType.GVT) {
            // Invalid selection - clear the selection
            this.selectedResourceType = null;
            this.dynamicSearchFields = [];
            this.dynamicFieldValues = {};
            return Promise.resolve();
        }

        this.selectedResourceType = resourceType;

        // Return a promise that resolves when the schema loads
        return new Promise<void>((resolve) => {
            // Subscribe to the schema loaded subject for this specific load
            const sub = this.schemaLoadedSubject$.subscribe(() => {
                sub.unsubscribe();
                resolve();
            });

            // Emit to the subject - switchMap will cancel any previous schema request
            this.resourceTypeSubject$.next(resourceType);
        });
    }

    handleSearchFields(searchFields?: SearchField[], schema?: any) {
        // Clear existing dynamic fields
        this.dynamicSearchFields = [];
        this.dynamicFieldValues = {};

        // Add new dynamic fields if they exist
        if (searchFields && searchFields.length > 0) {
            const mappedFields = searchFields
                .filter(field => !(field.nestedPaths)) // Only support a search field with a single nested path
                .map(field => {
                    let title = this.formatLabel(field.jsonPath);
                    let type = field.type || 'text';
                    if(schema) {
                        const oSchemaProperties = this._resolveSchemaFieldProperties(schema, field.jsonPath);
                        title = oSchemaProperties?.title || title;
                        type = oSchemaProperties?.type || type;

                    }

                    return {
                        jsonPath: field.jsonPath,
                        label: title + (this.getIntermediatePathParts(field.jsonPath) ?? ''),
                        type: type,
                        placeholder: field.placeholder || `Enter a value`,
                        description: field.description || `Filter resources by ${title}`,
                        nestedPaths: field.nestedPaths
                    };});

            // Remove duplicates based on jsonPath (keep first occurrence)
            const seen = new Set<string>();
            this.dynamicSearchFields = mappedFields.filter(field => {
                if (seen.has(field.jsonPath)) {
                    return false;
                }
                seen.add(field.jsonPath);
                return true;
            });

            // Initialize field values
            this.dynamicSearchFields.forEach(field => {
                this.dynamicFieldValues[field.jsonPath] = '';
            });
        }
    }

    _resolveSchemaFieldProperties(oJsonSchema:any, sJsonPath: string) {
        const aSegments = sJsonPath.split(".").filter(function (s) {
            return s !== "";
        });
        let oNode = oJsonSchema;
        for (let i = 0; i < aSegments.length; i++) {
            const sSegment = aSegments[i];
            if (!oNode) {
                return null;
            }
            if (sSegment === "*") {
                oNode = oNode.additionalProperties;
            } else {
                oNode = oNode.properties && oNode.properties[sSegment];
            }
        }
        if (!oNode) {
            return null;
        }
        return {type: oNode.type, enum: oNode.enum, title: oNode.title};
    }

    private formatLabel(jsonPath: string): string {
        // Convert jsonPath like "spec.productName" to "Product Name"
        // Takes only the last part of the path
        const parts = jsonPath.split('.').filter(p => p); // Remove empty strings from leading dots

        // Take only the last part
        return parts[parts.length - 1];
    }

    private getIntermediatePathParts(jsonPath: string): string | null {
        // Extract intermediate parts between 'spec' and the last field name
        const parts = jsonPath.split('.').filter(p => p);
        const relevantParts = parts[0] === 'spec' ? parts.slice(1) : parts;

        // Exclude the last part (the field name itself) - only get intermediate parts
        if (relevantParts.length > 1) {
            const intermediateParts = relevantParts.slice(0, -1);
            return ` (${intermediateParts
                .map(part => part.replace(/([A-Z])/g, ' $1').trim())
                .join('.')})`;
        }

        return null;
    }

    private escapeFieldSelectorValue(value: string): string {
        // Escape special characters that are reserved in field selector syntax
        // Based on the error message, we need to escape with backslash
        // Common reserved characters in query languages: ( ) , ~ space \
        return value.replace(/([()~,\\ ])/g, '\\$1');
    }

    onClose() {
        // Cancel any in-progress searches when closing advanced search
        this.cancelSearch.emit();
        this.close.emit();
    }

    onApply() {

        const advancedSearchTerms: any = {
            path: this.fullPath
        };

        const filters = this.filters;

        // Convert selectedResourceType object to GVT string before emitting
        if (this.selectedResourceType) {
            advancedSearchTerms.resourceSelector = `${this.selectedResourceType.GVT.group}/${this.selectedResourceType.GVT.version}/${this.selectedResourceType.GVT.type}`;
        }

        if (this.createdOnSelectedRange?.start) {
            advancedSearchTerms["afterCreationTime"] = this.isoDateToStartOfDay(this.createdOnDateRanges.start).toISOString();
        }
        if (this.createdOnSelectedRange?.end) {
            const endDate = this.isoDateToEndOfDay(this.createdOnDateRanges.end);
            advancedSearchTerms["beforeCreationTime"] = endDate.toISOString();
        }

        if (this.changedOnSelectedRange?.start) {
            advancedSearchTerms["afterRevision"] = this.isoDateToStartOfDay(this.changedOnDateRanges.start).getTime();
        }
        if (this.changedOnSelectedRange?.end) {
            const endDate = this.isoDateToEndOfDay(this.changedOnDateRanges.end);
            advancedSearchTerms["beforeRevision"] = endDate.getTime();
        }

        const fieldSelectors = [];
        if (filters.displayName) {
            const escapedDisplayName = this.escapeFieldSelectorValue(filters.displayName);
            fieldSelectors.push(`metadata.displayName in (\\~${escapedDisplayName})`);
        }
        if (filters.technicalName) {
            const escapedTechnicalName = this.escapeFieldSelectorValue(filters.technicalName);
            fieldSelectors.push(`metadata.name in (\\~${escapedTechnicalName})`);
        }

        // Add dynamic search fields to fieldSelectors
        this.dynamicSearchFields.forEach(field => {
            const value = this.dynamicFieldValues[field.jsonPath];
            if (value) {
                const jsonPath = field.jsonPath.substring(1); // Remove first character
                const escapedValue = this.escapeFieldSelectorValue(value);

                if(field.nestedPaths) {
                    // If we ever need to support multiple nested paths
                    fieldSelectors.push(`${jsonPath}$${field.nestedPaths.join(';')} anyOf (${escapedValue})`);
                } else {
                    fieldSelectors.push(`${jsonPath} in (${escapedValue})`);
                    fieldSelectors.push(`${jsonPath} anyOf (${escapedValue})`);
                }
            }
        });

        if(fieldSelectors.length) {
            advancedSearchTerms["fieldSelector"] = fieldSelectors.join(",");
        }

        if (this.filters.status && this.filters.status !== '') {
            advancedSearchTerms["status"] = this.filters.status;
        }
        if (filters.labelKey) {
            advancedSearchTerms["labelSelector"] = filters.labelKey;
        }
        if (filters.labelValues?.length > 0) {
            advancedSearchTerms["labelValue"] = filters.labelValues?.join(',');
        }

        if (this.filters.recursiveSearch !== undefined) {
            advancedSearchTerms["recursive"] = this.filters.recursiveSearch;
        }
        this.applyFilters.emit(advancedSearchTerms);

        // Only emit filtersModified if this was a user action (not during loading) and filters actually changed
        if (!this.isLoadingFilters) {
            const currentSnapshot = this.getFiltersSnapshot();
            if (currentSnapshot !== this.loadedFiltersSnapshot) {
                this.filtersModified.emit();
            }
        }
    }

    private getFiltersSnapshot(): string {
        return JSON.stringify({
            filters: this.filters,
            selectedResourceType: this.selectedResourceType?.GVT,
            dynamicFieldValues: this.dynamicFieldValues,
            createdOnDateRanges: this.createdOnDateRanges,
            changedOnDateRanges: this.changedOnDateRanges,
            labelValueTokens: this.labelValueTokens,
        });
    }

    onReset() {
        // Cancel any in-progress searches
        this.cancelSearch.emit();

        this.filters = {
            recursiveSearch: true,
            path: this.fullPath,
            status: '' // Default to "All Statuses"
        };
        this.selectedResourceType = null;
        this.dynamicSearchFields = [];
        this.dynamicFieldValues = {};
        this.filteredResourceTypes = this.resourceTypes.slice(0, MAX_RESOURCE_TYPES);
        this.labelValueTokens = [];
        this.labelValueInput = '';
        this.createdOnSelectedRange = null;
        this.changedOnSelectedRange = null;

        this.clearContent.emit();
        this.loadedFiltersSnapshot = null;
    }

    addLabelValueToken(event: Event) {
        event.preventDefault();
        const value = this.labelValueInput.trim();
        if (value && !this.labelValueTokens.includes(value)) {
            this.labelValueTokens.push(value);
            this.labelValueInput = '';
        } else if (value) {
            // Clear input if duplicate
            this.labelValueInput = '';
        }
    }

    removeLabelValueToken(index: number) {
        this.labelValueTokens.splice(index, 1);
    }

    onSaveView() {
        // Build organizationPath only if currentOrg is defined, otherwise leave it undefined
        const organizationPath = this.currentOrg ? `${this.currentOrg}${this.currentPath || ''}` : undefined;
        // Remove the * suffix if present to get the actual view name
        const currentViewNameWithoutAsterisk = this.currentViewName?.replace(/\*$/, '') || 'Standard';
        const dialogRef = this.dialogService.open(SaveFilterViewDialogComponent, {
            width: '500px',
            backdropClass: 'dialog-backdrop',
            data: {
                organizationPath: organizationPath,
                currentViewName: currentViewNameWithoutAsterisk
            }
        });

        dialogRef.afterClosed.subscribe({
            next: (result: { name: string; setAsDefault: boolean } | null) => {
                if (result) {
                    // Save the current filter configuration including resource type and dynamic fields
                    const filtersToSave = { ...this.filters };

                    // Include the selected resource type if one is selected
                    if (this.selectedResourceType) {
                        filtersToSave.resourceType = `${this.selectedResourceType.GVT.group}/${this.selectedResourceType.GVT.version}/${this.selectedResourceType.GVT.type}`;
                        filtersToSave.resourceTypeName = this.selectedResourceType.Name;
                    }

                    // Include date ranges
                    if (this.createdOnDateRanges.start) {
                        filtersToSave.createdOnStart = this.createdOnDateRanges.start;
                    }
                    if (this.createdOnDateRanges.end) {
                        filtersToSave.createdOnEnd = this.createdOnDateRanges.end;
                    }
                    if (this.changedOnDateRanges.start) {
                        filtersToSave.changedOnStart = this.changedOnDateRanges.start;
                    }
                    if (this.changedOnDateRanges.end) {
                        filtersToSave.changedOnEnd = this.changedOnDateRanges.end;
                    }

                    // Include dynamic field values
                    if (this.dynamicFieldValues && Object.keys(this.dynamicFieldValues).length > 0) {
                        filtersToSave.dynamicFieldValues = { ...this.dynamicFieldValues };
                    }

                    const organizationPath = this.currentOrg ? `${this.currentOrg}${this.currentPath || ''}` : undefined;
                    const success = this.savedFilterViewsService.saveView(
                        result.name,
                        filtersToSave,
                        organizationPath,
                        result.setAsDefault
                    );

                    if (success) {
                        console.log(`Filter view "${result.name}" saved successfully${result.setAsDefault ? ' as default' : ''}`);
                        // Emit event so parent can update the current view name
                        this.viewSaved.emit(result.name);
                    } else {
                        console.error(`Failed to save filter view "${result.name}"`);
                    }
                }
            },
            error: () => {
                // User cancelled
            }
        });
    }

    displayFunc(name: string): string {
        return name;
    }

    parseFunc(value: string): string {
        return value;
    }

    validateFunc(value: string): boolean {
        return value?.length >= 3;
    }

    valueFn(name: string): string {
        return name;
    }

    onLabelValuesKeyup(event: any): void {
        // Capture the input value as the user types
        if (event.target && event.target.value !== undefined) {
            this.labelValuePendingText = event.target.value;
        }
    }

    onLabelValuesBlur(): void {
        // Use the captured pending text since the input will be cleared by the time blur fires
        if (this.labelValuePendingText) {
            const inputText = this.labelValuePendingText.trim();
            if (inputText && this.validateFunc(inputText)) {
                // Add the input text as a token
                const currentValues = this.filters.labelValues || [];
                if (!currentValues.includes(inputText)) {
                    this.filters.labelValues = [...currentValues, inputText];
                }
            }
            // Clear the pending text
            this.labelValuePendingText = '';
        }
    }

    handleCreatedOnDateRangeChange(dates: DateRange<FdDate>): void {
        this.createdOnDateRanges.start = this.fdDateToIso(dates.start);
        this.createdOnDateRanges.end = this.fdDateToIso(dates.end);
    }

    handleChangedOnDateRangeChange(dates: DateRange<FdDate>): void {
        this.changedOnDateRanges.start = this.fdDateToIso(dates.start);
        this.changedOnDateRanges.end = this.fdDateToIso(dates.end);
    }

    private fdDateToIso(date: FdDate | null | undefined): string {
        if (!date) return '';
        const mm = String(date.month).padStart(2, '0');
        const dd = String(date.day).padStart(2, '0');
        return `${date.year}-${mm}-${dd}`;
    }

    private fdDateFromIso(iso: string): FdDate {
        const [year, month, day] = iso.split('-').map(Number);
        return new FdDate(year, month, day);
    }

    private isoDateToStartOfDay(iso: string): Date {
        const [year, month, day] = iso.split('-').map(Number);
        return new Date(year, month - 1, day, 0, 0, 0, 0);
    }

    private isoDateToEndOfDay(iso: string): Date {
        const [year, month, day] = iso.split('-').map(Number);
        return new Date(year, month - 1, day, 23, 59, 59, 999);
    }

    protected readonly Const = Const;
}
